/**
 * Modern Bold · <mb-promo-countdown> custom element
 * ─────────────────────────────────────────────────────────────────────────
 * Two surfaces for one evergreen offer, rendered by
 * sections/modern-promo-countdown.liquid:
 *
 *   • the dock — a floating chip (bottom-left, opposite <mb-crisp-launcher>)
 *     that counts down to midnight US Eastern and opens a card with the code
 *   • the exit-intent dialog — the same offer, centred, at most once per
 *     session, when the visitor looks like they are leaving
 *
 * Countdown
 *   Derived from the clock, never from a stored deadline: "today" rolls over
 *   at midnight America/New_York, so at 00:00 the timer simply reads 23h 59m
 *   again. Intl handles EST/EDT; a browser without timezone data falls back
 *   to a fixed UTC-5.
 *
 * Frequency (localStorage, so it holds across tabs and visits)
 *   The card's X silences both surfaces for whatever is left on the clock,
 *   so the chip comes back with the next day's offer. The exit dialog is
 *   rarer still: once seen, it stays away for a week.
 *
 * Exit intent
 *   fine pointer → the cursor leaving through the top of the viewport
 *   coarse pointer → the page flung back to the top after being read down,
 *     or 45s idle — both only once the visit looks real
 *   Either way: armed after a dwell, and shown at most once a week.
 *
 * Not colliding with the signup popup
 *   Both popups stamp POPUP_AT on open. The dialog stays shut while any
 *   modal is on screen and for COOLDOWN_MS after the last one — so a
 *   visitor is never interrupted twice in quick succession, while a later
 *   page in the same session can still catch them on the way out.
 *
 * DOM contract (rendered by the section):
 *   [data-mb-promo-dock]      floating chip wrapper
 *   [data-mb-promo-details]   the <details> the chip toggles
 *   [data-mb-promo-dismiss]   X inside the dock card; puts the offer away
 *   [data-mb-promo-clock]     every node the ticking digits are written into
 *   [data-mb-promo-copy]      code button (copies data-code)
 *   [data-mb-promo-status]    role="status" copy confirmation
 *   [data-mb-promo-modal]     exit dialog root
 *   [data-mb-promo-panel]     dialog card, tabindex="-1" focus target
 *   [data-mb-promo-close]     scrim, X and CTA inside the dialog
 *   data-code / data-section-id
 */

if (!customElements.get('mb-promo-countdown')) {
  const POPUP_AT = 'mb-popup-last-shown-at';
  const DISMISS_KEY = 'mb-promo-dismissed-until';
  const EXIT_KEY = 'mb-promo-exit-shown-until';

  const COOLDOWN_MS = 120000; // quiet window after any other popup
  const REVEAL_MS = 900; // chip entrance, kept off the critical path
  const ARM_MS = 6000; // dwell before exit intent counts
  const IDLE_MS = 45000; // coarse-pointer idle trigger
  const ENGAGED_MS = 20000; // …only once the visit looks real
  const EXIT_EVERY_MS = 7 * 24 * 60 * 60 * 1000; // exit dialog: once a week
  const CLOSE_TRANSITION_MS = 420;
  const DISMISS_TRANSITION_MS = 340; // dock fade-out before it leaves layout
  const COPIED_RESET_MS = 2600;
  const FLICK_PX_PER_MS = 2.5; // a real fling, not reading back up
  const VEL_SMOOTHING = 0.4; // weight of the newest sample in the velocity EMA
  const FLICK_WINDOW_MS = 1500; // how long a fling stays "recent"
  const SETTLE_MS = 180; // scrolling has stopped
  const MIN_DT_MS = 4; // below this a velocity sample is just noise
  const ENGAGED_SCROLL = 0.25; // share of the page that counts as engaged
  const LANDING = 0.08; // …and the fling has to come to rest at the top

  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

  // Storage throws in some privacy modes — a failed read degrades to "show
  // it", a failed write to "may show again on the next page".
  const readSession = (key) => {
    try { return sessionStorage.getItem(key); } catch { return null; }
  };
  const writeSession = (key, value) => {
    try { sessionStorage.setItem(key, value); } catch { /* ignore */ }
  };

  // Both "stay quiet" stamps — dismissed, and exit dialog already seen —
  // hold the time that surface is allowed back. localStorage, not
  // sessionStorage: the latter is per-TAB, so a second tab counted as a
  // fresh visitor and the dialog came back on every one of them.
  const isQuiet = (key) => {
    try { return Date.now() < (parseInt(localStorage.getItem(key) || '0', 10) || 0); }
    catch { return false; }
  };

  const nyClock = (() => {
    try {
      return new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/New_York',
        hour12: false,
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
    } catch {
      return null;
    }
  })();

  /** Seconds until the next midnight in New York. */
  const secondsLeft = () => {
    let h;
    let m;
    let s;
    if (nyClock) {
      const parts = {};
      for (const part of nyClock.formatToParts(new Date())) parts[part.type] = part.value;
      // Some engines render midnight as "24" under hour12:false.
      h = Number(parts.hour) % 24;
      m = Number(parts.minute);
      s = Number(parts.second);
    } else {
      const utc5 = new Date(Date.now() - 5 * 3600000);
      h = utc5.getUTCHours();
      m = utc5.getUTCMinutes();
      s = utc5.getUTCSeconds();
    }
    return 86400 - (h * 3600 + m * 60 + s);
  };

  const quietFor = (key, ms) => {
    try { localStorage.setItem(key, String(Date.now() + ms)); } catch { /* ignore */ }
  };

  const pad = (n) => String(n).padStart(2, '0');
  const format = (total) =>
    `${pad(Math.floor(total / 3600))}h ${pad(Math.floor((total % 3600) / 60))}m ${pad(total % 60)}s`;

  class MBPromoCountdown extends HTMLElement {
    #cleanups = [];
    #refs = {};
    #clocks = [];
    #lastActive = null;
    #tickId = null;
    #hideTimerId = null;
    #copyTimers = new WeakMap();
    #idleId = null;
    #settleId = null;
    #armed = false;
    #engaged = false;
    #deepest = 0;
    #lastY = 0;
    #lastT = 0;
    #velocity = 0;
    #flickAt = 0;

    connectedCallback() {
      this.#refs = {
        dock: this.querySelector('[data-mb-promo-dock]'),
        details: this.querySelector('[data-mb-promo-details]'),
        summary: this.querySelector('summary'),
        modal: this.querySelector('[data-mb-promo-modal]'),
        panel: this.querySelector('[data-mb-promo-panel]'),
      };
      this.#clocks = [...this.querySelectorAll('[data-mb-promo-clock]')];

      this.#bindDock();

      // Theme editor: the merchant drives the dialog by selecting the
      // section. Never auto-fire, never read or write their storage.
      if (window.Shopify?.designMode) {
        this.hidden = false;
        this.#startClock();
        this.#bindDesignMode();
        this.#reveal();
        return;
      }

      // One dismissal puts the whole offer away — chip and exit dialog
      // alike. Checked before anything starts, so a dismissed offer costs
      // nothing: no clock, no listeners, the host simply stays hidden.
      if (isQuiet(DISMISS_KEY)) return;

      this.hidden = false;
      this.#startClock();
      this.#bindModal();
      this.#reveal();
      this.#armExitIntent();
    }

    disconnectedCallback() {
      this.#cleanups.forEach((fn) => fn());
      this.#cleanups = [];
      clearInterval(this.#tickId);
      clearTimeout(this.#hideTimerId);
      clearTimeout(this.#idleId);
      clearTimeout(this.#settleId);
      for (const button of this.querySelectorAll('[data-mb-promo-copy]')) {
        clearTimeout(this.#copyTimers.get(button));
      }
    }

    #on(target, type, handler, options) {
      target.addEventListener(type, handler, options);
      this.#cleanups.push(() => target.removeEventListener(type, handler, options));
    }

    // ── Countdown ──────────────────────────────────────────────────────────

    // Every tick recomputes from the clock rather than decrementing, so a
    // throttled background tab or a sleeping laptop comes back correct.
    #startClock() {
      const paint = () => {
        const text = format(secondsLeft());
        for (const node of this.#clocks) node.textContent = text;
      };
      paint();
      this.#tickId = setInterval(paint, 1000);
      this.#on(document, 'visibilitychange', () => {
        if (!document.hidden) paint();
      });
    }

    #reveal() {
      const id = setTimeout(() => this.classList.add('is-ready'), REVEAL_MS);
      this.#cleanups.push(() => clearTimeout(id));
    }

    #dismiss() {
      if (!window.Shopify?.designMode) quietFor(DISMISS_KEY, secondsLeft() * 1000);

      // The button about to disappear is holding focus; hand it on first, or
      // it drops to <body> the moment the dock leaves the page.
      document.getElementById('MainContent')?.focus();

      // Dropping .is-ready runs the entrance transition in reverse; the host
      // only leaves layout once that has played out.
      this.classList.remove('is-ready');
      const id = setTimeout(() => {
        this.hidden = true;
        clearInterval(this.#tickId);
      }, DISMISS_TRANSITION_MS);
      this.#cleanups.push(() => clearTimeout(id));
    }

    // ── Dock ───────────────────────────────────────────────────────────────

    #bindDock() {
      this.#on(this, 'click', (e) => {
        if (e.target.closest('[data-mb-promo-copy]')) {
          this.#copy(e.target.closest('[data-mb-promo-copy]'));
          return;
        }
        if (e.target.closest('[data-mb-promo-dismiss]')) this.#dismiss();
      });
    }

    async #copy(button) {
      const code = this.dataset.code || '';
      const row = button.closest('.mb-promo__code-row');
      const label = button.querySelector('.mb-promo__code-text');
      const status = row?.querySelector('[data-mb-promo-status]');
      let copied = false;

      try {
        await navigator.clipboard.writeText(code);
        copied = true;
      } catch {
        copied = false;
      }

      if (copied) {
        // The label itself is the confirmation, then it turns back into the
        // code. The button is a full-width flex item, so the shorter text
        // swaps in without the card resizing around it.
        button.classList.add('is-copied');
        if (label) label.textContent = button.dataset.copiedLabel || 'Copied!';
        clearTimeout(this.#copyTimers.get(button));
        this.#copyTimers.set(button, setTimeout(() => {
          button.classList.remove('is-copied');
          if (label) label.textContent = code;
        }, COPIED_RESET_MS));
      } else {
        // No clipboard permission (or an insecure context) — leave the code on
        // screen and select it so the shopper can copy it with the OS gesture.
        this.#selectText(label);
      }

      if (status) status.textContent = copied ? `Code ${code} copied` : `Code ${code} selected — copy it now`;
    }

    #selectText(node) {
      if (!node) return;
      const range = document.createRange();
      range.selectNodeContents(node);
      const selection = window.getSelection();
      selection?.removeAllRanges();
      selection?.addRange(range);
    }

    // ── Exit intent ────────────────────────────────────────────────────────

    #armExitIntent() {
      if (isQuiet(EXIT_KEY)) return;

      const armId = setTimeout(() => { this.#armed = true; }, ARM_MS);
      this.#cleanups.push(() => clearTimeout(armId));

      if (window.matchMedia('(pointer: coarse)').matches) {
        this.#armCoarse();
      } else {
        // relatedTarget is null only when the cursor leaves the window
        // itself; the top edge is the one that leads to the tab bar.
        this.#on(document, 'mouseout', (e) => {
          if (e.relatedTarget || e.clientY > 6) return;
          this.#openModal();
        });
      }
    }

    // Touch has no "cursor left the page" event, so the signal is behavioural:
    // a fast flick back to the top (reaching for the address bar or Back), or
    // going quiet altogether — both only once the visit looks real.
    #armCoarse() {
      const engagedId = setTimeout(() => { this.#engaged = true; }, ENGAGED_MS);
      this.#cleanups.push(() => clearTimeout(engagedId));

      const scrollable = () => document.documentElement.scrollHeight - window.innerHeight;

      this.#lastY = window.scrollY;
      this.#lastT = performance.now();

      this.#on(window, 'scroll', () => {
        const y = window.scrollY;
        const t = performance.now();

        const range = scrollable();
        const progress = range > 0 ? y / range : 0;
        this.#deepest = Math.max(this.#deepest, progress);
        if (this.#deepest >= ENGAGED_SCROLL) this.#engaged = true;

        // Momentum scrolling fires continuously and decelerates into the
        // top, so speed at the moment of arrival is always low. Mark the
        // fling when it happens, then judge where it came to rest.
        // MIN_DT only drops degenerate samples. It must stay well under one
        // frame: scroll events arrive per refresh, so a 16ms floor threw away
        // every sample on a 120Hz phone and the trigger never fired at all.
        // The EMA is what separates a fling from a drag — a single jittery
        // frame during an ordinary scroll back up clears the raw threshold,
        // only a sustained one clears the smoothed velocity.
        const dt = t - this.#lastT;
        if (dt >= MIN_DT_MS) {
          const v = (this.#lastY - y) / dt; // positive = travelling upward
          this.#velocity = this.#velocity * (1 - VEL_SMOOTHING) + v * VEL_SMOOTHING;
          if (this.#velocity >= FLICK_PX_PER_MS) this.#flickAt = t;
        }
        this.#lastY = y;
        this.#lastT = t;

        this.#restartIdle();

        clearTimeout(this.#settleId);
        this.#settleId = setTimeout(() => {
          const flungRecently = this.#flickAt && performance.now() - this.#flickAt <= FLICK_WINDOW_MS;
          // Read their way down, then threw the page back to the very top:
          // the touch equivalent of reaching for the address bar.
          if (flungRecently && progress <= LANDING && this.#deepest >= ENGAGED_SCROLL) {
            this.#openModal();
          }
        }, SETTLE_MS);
      }, { passive: true });

      for (const type of ['touchstart', 'keydown', 'click']) {
        this.#on(document, type, () => this.#restartIdle(), { passive: true });
      }
      this.#restartIdle();
    }

    #restartIdle() {
      clearTimeout(this.#idleId);
      this.#idleId = setTimeout(() => {
        if (this.#engaged) this.#openModal();
      }, IDLE_MS);
    }

    // ── Exit dialog ────────────────────────────────────────────────────────

    #bindModal() {
      this.#on(this, 'click', (e) => {
        if (e.target.closest('[data-mb-promo-close]')) this.#closeModal();
      });
      this.#on(document, 'keydown', (e) => {
        if (e.key === 'Escape' && this.#refs.modal?.classList.contains('is-open')) this.#closeModal();
      });
      this.#on(this, 'keydown', (e) => this.#trapFocus(e));
    }

    #bindDesignMode() {
      const matches = (e) => e.detail?.sectionId === this.dataset.sectionId;
      this.#on(document, 'shopify:section:select', (e) => {
        if (matches(e)) this.#showModal();
      });
      this.#on(document, 'shopify:section:deselect', (e) => {
        if (matches(e)) this.#closeModal({ restoreFocus: false });
      });
      this.#on(this, 'click', (e) => {
        if (e.target.closest('[data-mb-promo-close]')) this.#closeModal({ restoreFocus: false });
      });
    }

    #openModal() {
      const { modal } = this.#refs;
      if (!modal || modal.classList.contains('is-open')) return;
      if (!this.#armed || isQuiet(EXIT_KEY)) return;

      // Never the second interruption in a row: something else on screen, or
      // another popup too recently.
      const lastAt = parseInt(readSession(POPUP_AT) || '0', 10) || 0;
      if (lastAt && Date.now() - lastAt < COOLDOWN_MS) return;
      if (
        document.body.classList.contains('mb-popup-open') ||
        document.body.classList.contains('mb-search-open') ||
        document.querySelector('cart-drawer.active')
      ) return;

      quietFor(EXIT_KEY, EXIT_EVERY_MS);
      writeSession(POPUP_AT, String(Date.now()));
      this.#showModal();
    }

    #showModal() {
      const { modal, panel } = this.#refs;
      if (!modal) return;

      // A reopen inside the close transition would let the stale hide timer
      // fire and leave the dialog is-open but display:none.
      clearTimeout(this.#hideTimerId);
      this.#lastActive = document.activeElement;
      modal.hidden = false;
      modal.offsetHeight; // reflow, so the fade starts from opacity 0
      modal.classList.add('is-open');
      document.body.classList.add('mb-popup-open');

      // The panel, not the code button: AT hears the dialog's heading and
      // the offer before landing on a control.
      requestAnimationFrame(() => panel?.focus());
    }

    #closeModal({ restoreFocus = true } = {}) {
      const { modal } = this.#refs;
      if (!modal?.classList.contains('is-open')) return;

      modal.classList.remove('is-open');
      document.body.classList.remove('mb-popup-open');
      this.#hideTimerId = setTimeout(() => { modal.hidden = true; }, CLOSE_TRANSITION_MS);

      if (!restoreFocus) return;
      const previous = this.#lastActive;
      if (previous && previous !== document.body && previous.isConnected && typeof previous.focus === 'function') {
        previous.focus();
      } else {
        document.getElementById('MainContent')?.focus();
      }
    }

    #trapFocus(e) {
      const { modal } = this.#refs;
      if (e.key !== 'Tab' || !modal?.classList.contains('is-open')) return;

      // tabIndex >= 0 drops the pointer-only scrim; without that, Shift+Tab
      // from the first control walked out of the dialog into the page.
      const focusable = [...modal.querySelectorAll(FOCUSABLE)].filter(
        (el) => el.tabIndex >= 0 && !el.hasAttribute('hidden') && !el.closest('[hidden]')
      );
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable.at(-1);

      // Focus legitimately sits on the tabindex="-1" panel right after open —
      // outside the list, so wrap explicitly instead of letting the browser
      // tab out of an aria-modal dialog.
      if (!focusable.includes(document.activeElement)) {
        e.preventDefault();
        (e.shiftKey ? last : first).focus();
        return;
      }
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  customElements.define('mb-promo-countdown', MBPromoCountdown);
}
